import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, FileText, ExternalLink, LifeBuoy } from "lucide-react";

const LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_equity-support/artifacts/114243fe_edi%20logo.png";

const GUIDES = [
    { icon: FileText, title: "Understanding workplace concerns", desc: "How to recognise discrimination, bullying and unfair processes — and the language to describe what is happening." },
    { icon: LifeBuoy, title: "Knowing where to turn", desc: "A plain-English overview of HR, Freedom to Speak Up Guardians, trade unions and when each route applies." },
    { icon: BookOpen, title: "Preparing for difficult conversations", desc: "Practical steps to prepare for meetings, keep records and protect your wellbeing throughout a process." },
];

const LINKS = [
    { label: "NHS Workforce Race Equality Standard (WRES)", href: "https://www.england.nhs.uk/about/equality/equality-hub/workforce-equality-data-standards/equality-standard/" },
    { label: "NHS Freedom to Speak Up", href: "https://nationalguardian.org.uk/" },
    { label: "ACAS — Discrimination and the law", href: "https://www.acas.org.uk/discrimination-and-the-law" },
];

export default function Resources() {
    return (
        <div className="bg-[#FBF7F0] text-[#14203A] font-sans min-h-screen" data-testid="resources-page">
            <header className="border-b border-[#eee3d0] bg-[#FBF7F0]/90 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center" data-testid="resources-logo"><img src={LOGO} alt="EDI Connect" className="h-14 w-auto" /></Link>
                    <Link to="/" className="inline-flex items-center gap-2 text-[#14203A] font-semibold text-sm" data-testid="resources-back"><ArrowLeft className="h-4 w-4" /> Back to home</Link>
                </div>
            </header>

            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <span className="text-[#C58B2B] font-semibold text-sm uppercase tracking-wider">Resources</span>
                <h1 className="font-display text-4xl sm:text-5xl font-bold mt-3 leading-tight">Guides & support for health and social care professionals</h1>
                <p className="mt-5 text-[#3d4657] leading-relaxed max-w-3xl">Clear, practical resources to help you understand what is happening at work, explore your options and access the right support. We're adding new guides regularly — check back soon.</p>

                <div className="mt-12 grid md:grid-cols-3 gap-6">
                    {GUIDES.map((g) => (
                        <div key={g.title} className="bg-white rounded-2xl border border-[#eee3d0] p-7" data-testid="resource-card">
                            <div className="h-12 w-12 rounded-xl bg-[#14203A] flex items-center justify-center"><g.icon className="h-5 w-5 text-[#E9C77E]" /></div>
                            <h3 className="font-display text-xl font-bold text-[#14203A] mt-5">{g.title}</h3>
                            <p className="mt-2 text-sm text-[#3d4657] leading-relaxed">{g.desc}</p>
                            <span className="mt-4 inline-block text-xs font-semibold uppercase tracking-wider text-[#C58B2B]">Coming soon</span>
                        </div>
                    ))}
                </div>

                <div className="mt-14 bg-white rounded-2xl border border-[#eee3d0] p-8">
                    <h2 className="font-display text-2xl font-bold text-[#14203A]">Useful external links</h2>
                    <ul className="mt-5 space-y-3">
                        {LINKS.map((l) => (
                            <li key={l.href}>
                                <a href={l.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#14203A] font-medium hover:text-[#C58B2B] transition-colors duration-200" data-testid="resource-link">
                                    <ExternalLink className="h-4 w-4 text-[#C58B2B]" /> {l.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-12 bg-[#14203A] text-white rounded-2xl p-8 text-center">
                    <h2 className="font-display text-2xl font-bold">Need support now?</h2>
                    <p className="mt-3 text-slate-300 max-w-2xl mx-auto">Start with a confidential conversation. You do not need to know which service you need.</p>
                    <Link to="/#contact"><Button size="lg" className="mt-6 bg-[#E9C77E] hover:bg-[#d9b566] text-[#14203A] font-semibold rounded-full px-8" data-testid="resources-contact">Get in touch</Button></Link>
                </div>
            </section>

            <footer className="bg-[#14203A] text-slate-400 py-6 text-center text-xs">© {new Date().getFullYear()} Coach Write Speak · Herbie Nyathi. All rights reserved.</footer>
        </div>
    );
}
